import { IHomeStatistics } from "domain/Utils/models/IHomeStatistics";
import { SBTranslate } from "i18n";
import { useMemo } from "react";
import {
	formatCurrencyShort,
	formatDecimal,
	minmax,
	percentageFormatter,
} from "utils/formatters";

export default ({ statistics, isMonthly, isCost }: useForecastStatsProps) => {
	const evaluation = isMonthly
		? statistics?.evaluation?.monthlyEvaluation
		: statistics?.evaluation?.dailyEvaluation;

	const benchmarkEvaluation = isMonthly
		? statistics?.benchmarkEvaluation?.monthlyEvaluation
		: statistics?.benchmarkEvaluation?.dailyEvaluation;

	const avgDemand = isMonthly
		? statistics?.evaluation?.monthlyAvgDemand
		: statistics?.evaluation?.avgDemand;

	const stdDev = isMonthly
		? statistics?.evaluation?.monthlyDemandStdDev
		: statistics?.evaluation?.demandStdDev;

	const forecastSum = isCost
		? statistics?.evaluation?.forecastSumCost
		: statistics?.evaluation?.forecastSum;

	const benchmarkForecastSum = isCost
		? statistics?.benchmarkEvaluation?.forecastSumCost
		: statistics?.benchmarkEvaluation?.forecastSum;

	const stats = useMemo(() => {
		return [
			{
				tooltip: SBTranslate("tips.demand"),
				label: `${SBTranslate("average")}`,
				model: {
					forecast: {
						value: avgDemand?.value,
						range: `± ${formatDecimal(stdDev)} ${SBTranslate(
							"deviation"
						)}`,
					},
				},
				formatter: formatDecimal,
				toCompare: false,
			},
			{
				tooltip: "",
				label: SBTranslate("forecast"),
				model: {
					forecast: {
						value: forecastSum.value,
						range: "",
					},
					forecastBenchmark: {
						value: benchmarkForecastSum.value,
						range: "",
					},
				},
				formatter: isCost ? formatCurrencyShort : formatDecimal,
				toCompare: true,
			},
			{
				tooltip: SBTranslate("tips.rmse"),
				label: `RMSE - ${SBTranslate("deviation")}`,
				model: {
					forecast: {
						value: evaluation?.rmse,
						range: minmax(
							formatDecimal(
								isMonthly
									? statistics?.evaluation
											?.monthlyEvaluationMin?.rmse
									: statistics?.evaluation?.dailyEvaluationMin
											?.rmse
							),
							formatDecimal(
								isMonthly
									? statistics?.evaluation
											?.monthlyEvaluationMax?.rmse
									: statistics?.evaluation?.dailyEvaluationMax
											?.rmse
							)
						).toUpperCase(),
					},
					forecastBenchmark: {
						value: benchmarkEvaluation?.rmse,
						range: minmax(
							formatDecimal(
								isMonthly
									? statistics?.benchmarkEvaluation
											?.monthlyEvaluationMin?.rmse
									: statistics?.benchmarkEvaluation
											?.dailyEvaluationMin?.rmse
							),
							formatDecimal(
								isMonthly
									? statistics?.benchmarkEvaluation
											?.monthlyEvaluationMax?.rmse
									: statistics?.benchmarkEvaluation
											?.dailyEvaluationMax?.rmse
							)
						).toUpperCase(),
					},
				},
				formatter: formatDecimal,
				toCompare: true,
			},
			{
				tooltip: SBTranslate("tips.r2"),
				label: `r - ${SBTranslate("explainability")}`,
				model: {
					forecast: {
						value: evaluation?.r2,
						range: minmax(
							percentageFormatter(
								isMonthly
									? statistics?.evaluation
											?.monthlyEvaluationMin?.r2
									: statistics?.evaluation?.dailyEvaluationMin
											?.r2
							),
							percentageFormatter(
								isMonthly
									? statistics?.evaluation
											?.monthlyEvaluationMax?.r2
									: statistics?.evaluation?.dailyEvaluationMax
											?.r2
							)
						).toUpperCase(),
					},
					forecastBenchmark: {
						value: benchmarkEvaluation?.r2,
						range: minmax(
							percentageFormatter(
								isMonthly
									? statistics?.benchmarkEvaluation
											?.monthlyEvaluationMin?.r2
									: statistics?.benchmarkEvaluation
											?.dailyEvaluationMin?.r2
							),
							percentageFormatter(
								isMonthly
									? statistics?.benchmarkEvaluation
											?.monthlyEvaluationMax?.r2
									: statistics?.benchmarkEvaluation
											?.dailyEvaluationMax?.r2
							)
						).toUpperCase(),
					},
				},
				formatter: percentageFormatter,
				toCompare: true,
			},
			{
				tooltip: SBTranslate("tips.mpe"),
				label: `PE - ${SBTranslate("bias")}`,
				model: {
					forecast: {
						value: evaluation?.mpe,
						range: minmax(
							percentageFormatter(
								isMonthly
									? statistics?.evaluation
											?.monthlyEvaluationMin?.mpe
									: statistics?.evaluation?.dailyEvaluationMin
											?.mpe
							),
							percentageFormatter(
								isMonthly
									? statistics?.evaluation
											?.monthlyEvaluationMax?.mpe
									: statistics?.evaluation?.dailyEvaluationMax
											?.mpe
							)
						).toUpperCase(),
					},
					forecastBenchmark: {
						value: benchmarkEvaluation?.mpe,
						range: minmax(
							percentageFormatter(
								isMonthly
									? statistics?.benchmarkEvaluation
											?.monthlyEvaluationMin?.mpe
									: statistics?.benchmarkEvaluation
											?.dailyEvaluationMin?.mpe
							),
							percentageFormatter(
								isMonthly
									? statistics?.benchmarkEvaluation
											?.monthlyEvaluationMax?.mpe
									: statistics?.benchmarkEvaluation
											?.dailyEvaluationMax?.mpe
							)
						).toUpperCase(),
					},
				},
				formatter: percentageFormatter,
				toCompare: true,
			},
			{
				tooltip: SBTranslate("tips.mape"),
				label: `APE - ${SBTranslate("error")}`,
				model: {
					forecast: {
						value: evaluation?.mape,
						range: minmax(
							percentageFormatter(
								isMonthly
									? statistics?.evaluation
											?.monthlyEvaluationMin?.mape
									: statistics?.evaluation?.dailyEvaluationMin
											?.mape
							),
							percentageFormatter(
								isMonthly
									? statistics?.evaluation
											?.monthlyEvaluationMax?.mape
									: statistics?.evaluation?.dailyEvaluationMax
											?.mape
							)
						).toUpperCase(),
					},
					forecastBenchmark: {
						value: benchmarkEvaluation?.mape,
						range: minmax(
							percentageFormatter(
								isMonthly
									? statistics?.benchmarkEvaluation
											?.monthlyEvaluationMin?.mape
									: statistics?.benchmarkEvaluation
											?.dailyEvaluationMin?.mape
							),
							percentageFormatter(
								isMonthly
									? statistics?.benchmarkEvaluation
											?.monthlyEvaluationMax?.mape
									: statistics?.benchmarkEvaluation
											?.dailyEvaluationMax?.mape
							)
						).toUpperCase(),
					},
				},
				formatter: percentageFormatter,
				toCompare: true,
			},
			{
				tooltip: SBTranslate("tips.cv"),
				label: `CV - ${SBTranslate("volatility")}`,
				model: {
					forecast: {
						value: evaluation?.cv,
						range: minmax(
							formatDecimal(
								isMonthly
									? statistics?.evaluation
											?.monthlyEvaluationMin?.cv
									: statistics?.evaluation?.dailyEvaluationMin
											?.cv
							),
							formatDecimal(
								isMonthly
									? statistics?.evaluation
											?.monthlyEvaluationMax?.cv
									: statistics?.evaluation?.dailyEvaluationMax
											?.cv
							)
						).toUpperCase(),
					},
				},
				formatter: formatDecimal,
				toCompare: false,
			},
		];
	}, [statistics, isMonthly, isCost]);
	return { stats };
};

interface useForecastStatsProps {
	isMonthly: boolean;
	statistics: IHomeStatistics;
	isCost: boolean;
}
